// Exponential and Logarithmic Functions Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const exponentialLogarithmSamples = {
  fundamental: [
    {
      id: 'exp_log_fund_01',
      problem: "Solve 2^x = 8",
      problemType: "exponential_logarithm",
      difficulty: "fundamental",
      description: "Basic exponential equation",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with variable in exponent",
            value: "2^x = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 8 as a power of 2",
            value: "8 = 2³",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_comparison",
            name: "Exponent Comparison",
            type: "values",
            description: "Compare exponents",
            value: "x = 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 8 as a power of 2",
            value: "Step 2: 8 = 2³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 2^x = 2³, so x = 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_solution",
            name: "Write Solution",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: x = 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_comparison", type: "gives", strength: 10 },
          { from: "exponent_comparison", to: "solution", type: "equals", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "write_solution", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_fund_02',
      problem: "Solve 3^x = 27",
      problemType: "exponential_logarithm",
      difficulty: "fundamental",
      description: "Basic exponential equation",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with variable in exponent",
            value: "3^x = 27",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "27",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 27 as a power of 3",
            value: "27 = 3³",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_comparison",
            name: "Exponent Comparison",
            type: "values",
            description: "Compare exponents",
            value: "x = 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 27 as a power of 3",
            value: "Step 2: 27 = 3³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 3^x = 3³, so x = 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_solution",
            name: "Write Solution",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: x = 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_comparison", type: "gives", strength: 10 },
          { from: "exponent_comparison", to: "solution", type: "equals", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "write_solution", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_fund_03',
      problem: "Solve log₂(x) = 4",
      problemType: "exponential_logarithm",
      difficulty: "fundamental",
      description: "Basic logarithmic equation",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with logarithm",
            value: "log₂(x) = 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the logarithm",
            value: "2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "The value of the logarithm",
            value: "4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x = 2^4",
            value: "x = 2^4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the power",
            value: "x = 16",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 16",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the logarithm",
            value: "Step 1: Base is 2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_exponent",
            name: "Identify Exponent",
            type: "steps",
            description: "Recognize the value of the logarithm",
            value: "Step 2: Exponent is 4",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x = 2^4",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "calculate_result",
            name: "Calculate Result",
            type: "steps",
            description: "Calculate the power",
            value: "Step 4: x = 16",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "base", type: "has", strength: 9 },
          { from: "logarithmic_equation", to: "exponent", type: "equals", strength: 9 },
          { from: "base", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponent", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponential_form", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_fund_04',
      problem: "Solve 5^x = 125",
      problemType: "exponential_logarithm",
      difficulty: "fundamental",
      description: "Basic exponential equation",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with variable in exponent",
            value: "5^x = 125",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "5",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "125",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 125 as a power of 5",
            value: "125 = 5³",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_comparison",
            name: "Exponent Comparison",
            type: "values",
            description: "Compare exponents",
            value: "x = 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 125 as a power of 5",
            value: "Step 2: 125 = 5³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 5^x = 5³, so x = 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_solution",
            name: "Write Solution",
            type: "steps",
            description: "State the final answer",
            value: "Step 4: x = 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_comparison", type: "gives", strength: 10 },
          { from: "exponent_comparison", to: "solution", type: "equals", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "write_solution", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_fund_05',
      problem: "Solve log₃(x) = 2",
      problemType: "exponential_logarithm",
      difficulty: "fundamental",
      description: "Basic logarithmic equation",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with logarithm",
            value: "log₃(x) = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the logarithm",
            value: "3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "The value of the logarithm",
            value: "2",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x = 3^2",
            value: "x = 3^2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the power",
            value: "x = 9",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 9",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the logarithm",
            value: "Step 1: Base is 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_exponent",
            name: "Identify Exponent",
            type: "steps",
            description: "Recognize the value of the logarithm",
            value: "Step 2: Exponent is 2",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x = 3^2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "calculate_result",
            name: "Calculate Result",
            type: "steps",
            description: "Calculate the power",
            value: "Step 4: x = 9",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "base", type: "has", strength: 9 },
          { from: "logarithmic_equation", to: "exponent", type: "equals", strength: 9 },
          { from: "logarithm_to_exponential", to: "exponential_form", type: "gives", strength: 10 },
          { from: "base", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponent", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponential_form", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "solution", type: "equals", strength: 10 },
          { from: "identify_base", to: "identify_exponent", type: "enables", strength: 8 },
          { from: "identify_exponent", to: "convert_to_exponential", type: "enables", strength: 9 },
          { from: "convert_to_exponential", to: "calculate_result", type: "leads to", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'exp_log_inter_01',
      problem: "Solve 2^(x+1) = 16",
      problemType: "exponential_logarithm",
      difficulty: "intermediate",
      description: "Exponential equation with variable expression",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with variable expression in exponent",
            value: "2^(x+1) = 16",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "16",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 16 as a power of 2",
            value: "16 = 2^4",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_equation",
            name: "Exponent Equation",
            type: "values",
            description: "Set exponents equal",
            value: "x + 1 = 4",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = 3",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 16 as a power of 2",
            value: "Step 2: 16 = 2^4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 2^(x+1) = 2^4, so x+1 = 4",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the linear equation",
            value: "Step 4: x = 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_equation", type: "gives", strength: 10 },
          { from: "exponent_equation", to: "solution", type: "solves", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_inter_02',
      problem: "Solve log₂(x-1) = 3",
      problemType: "exponential_logarithm",
      difficulty: "intermediate",
      description: "Logarithmic equation with variable expression",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with variable expression in logarithm",
            value: "log₂(x-1) = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the logarithm",
            value: "2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "The value of the logarithm",
            value: "3",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x-1 = 2^3",
            value: "x-1 = 2^3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate and solve",
            value: "x-1 = 8, x = 9",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of x",
            value: "x = 9",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the logarithm",
            value: "Step 1: Base is 2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_exponent",
            name: "Identify Exponent",
            type: "steps",
            description: "Recognize the value of the logarithm",
            value: "Step 2: Exponent is 3",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x-1 = 2^3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the linear equation",
            value: "Step 4: x = 9",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "base", type: "has", strength: 9 },
          { from: "logarithmic_equation", to: "exponent", type: "equals", strength: 9 },
          { from: "logarithm_to_exponential", to: "exponential_form", type: "gives", strength: 10 },
          { from: "base", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponent", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponential_form", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "solution", type: "equals", strength: 10 },
          { from: "identify_base", to: "identify_exponent", type: "enables", strength: 8 },
          { from: "identify_exponent", to: "convert_to_exponential", type: "enables", strength: 9 },
          { from: "convert_to_exponential", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_inter_03',
      problem: "Solve 3^(2x) = 81",
      problemType: "exponential_logarithm",
      difficulty: "intermediate",
      description: "Exponential equation with coefficient in exponent",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with coefficient in exponent",
            value: "3^(2x) = 81",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "81",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 81 as a power of 3",
            value: "81 = 3^4",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_equation",
            name: "Exponent Equation",
            type: "values",
            description: "Set exponents equal",
            value: "2x = 4",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 81 as a power of 3",
            value: "Step 2: 81 = 3^4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 3^(2x) = 3^4, so 2x = 4",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the linear equation",
            value: "Step 4: x = 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_equation", type: "gives", strength: 10 },
          { from: "exponent_equation", to: "solution", type: "solves", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_inter_04',
      problem: "Solve log₅(x²) = 2",
      problemType: "exponential_logarithm",
      difficulty: "intermediate",
      description: "Logarithmic equation with quadratic argument",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with quadratic argument in logarithm",
            value: "log₅(x²) = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the logarithm",
            value: "5",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "The value of the logarithm",
            value: "2",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x² = 5^2",
            value: "x² = 5^2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate and solve",
            value: "x² = 25, x = ±5",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The values of x",
            value: "x = 5 or x = -5",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the logarithm",
            value: "Step 1: Base is 5",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_exponent",
            name: "Identify Exponent",
            type: "steps",
            description: "Recognize the value of the logarithm",
            value: "Step 2: Exponent is 2",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x² = 5^2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_quadratic",
            name: "Solve Quadratic",
            type: "steps",
            description: "Solve the quadratic equation",
            value: "Step 4: x = ±5",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "base", type: "has", strength: 9 },
          { from: "logarithmic_equation", to: "exponent", type: "equals", strength: 9 },
          { from: "logarithm_to_exponential", to: "exponential_form", type: "gives", strength: 10 },
          { from: "base", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponent", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponential_form", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "solution", type: "equals", strength: 10 },
          { from: "identify_base", to: "identify_exponent", type: "enables", strength: 8 },
          { from: "identify_exponent", to: "convert_to_exponential", type: "enables", strength: 9 },
          { from: "convert_to_exponential", to: "solve_quadratic", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_inter_05',
      problem: "Solve 4^(x-2) = 64",
      problemType: "exponential_logarithm",
      difficulty: "intermediate",
      description: "Exponential equation with subtraction in exponent",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with subtraction in exponent",
            value: "4^(x-2) = 64",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the exponential",
            value: "4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "concepts",
            description: "The value on the right side",
            value: "64",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 64 as a power of 4",
            value: "64 = 4^3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_equation",
            name: "Exponent Equation",
            type: "values",
            description: "Set exponents equal",
            value: "x - 2 = 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = 5",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the exponential",
            value: "Step 1: Base is 4",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "rewrite_result",
            name: "Rewrite Result as Power",
            type: "steps",
            description: "Express 64 as a power of 4",
            value: "Step 2: 64 = 4^3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 4^(x-2) = 4^3, so x-2 = 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the linear equation",
            value: "Step 4: x = 5",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base", type: "has", strength: 9 },
          { from: "exponential_equation", to: "result", type: "equals", strength: 9 },
          { from: "result", to: "power_identification", type: "rewritten as", strength: 10 },
          { from: "power_identification", to: "exponent_equation", type: "gives", strength: 10 },
          { from: "exponent_equation", to: "solution", type: "solves", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_base", to: "rewrite_result", type: "enables", strength: 8 },
          { from: "rewrite_result", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'exp_log_hard_01',
      problem: "Solve 2^(x+1) + 2^x = 12",
      problemType: "exponential_logarithm",
      difficulty: "harder",
      description: "Exponential equation with multiple terms",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with multiple exponential terms",
            value: "2^(x+1) + 2^x = 12",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "exponent_law",
            name: "Exponent Law",
            type: "formulas",
            description: "2^(x+1) = 2^x × 2^1 = 2 × 2^x",
            value: "2^(x+1) = 2 × 2^x",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute back into equation",
            value: "2 × 2^x + 2^x = 12",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "factorization",
            name: "Factorization",
            type: "values",
            description: "Factor out 2^x",
            value: "2^x(2 + 1) = 12",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Simplify the equation",
            value: "2^x × 3 = 12, 2^x = 4",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_terms",
            name: "Identify Terms",
            type: "steps",
            description: "Recognize the two exponential terms",
            value: "Step 1: 2^(x+1) and 2^x",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_exponent_law",
            name: "Apply Exponent Law",
            type: "steps",
            description: "Use the exponent law to simplify",
            value: "Step 2: 2^(x+1) = 2 × 2^x",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "factor_common_term",
            name: "Factor Common Term",
            type: "steps",
            description: "Factor out the common 2^x",
            value: "Step 3: 2^x(2 + 1) = 12",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_simplified",
            name: "Solve Simplified",
            type: "steps",
            description: "Solve the simplified equation",
            value: "Step 4: 2^x = 4, so x = 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "exponent_law", type: "simplified using", strength: 9 },
          { from: "exponent_law", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "factorization", type: "simplified by", strength: 10 },
          { from: "factorization", to: "simplification", type: "gives", strength: 10 },
          { from: "simplification", to: "solution", type: "solves", strength: 10 },
          { from: "identify_terms", to: "apply_exponent_law", type: "enables", strength: 8 },
          { from: "apply_exponent_law", to: "factor_common_term", type: "enables", strength: 9 },
          { from: "factor_common_term", to: "solve_simplified", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_hard_02',
      problem: "Solve log₂(x) + log₂(x-2) = 3",
      problemType: "exponential_logarithm",
      difficulty: "harder",
      description: "Logarithmic equation with sum of logarithms",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with sum of logarithms",
            value: "log₂(x) + log₂(x-2) = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "logarithm_law",
            name: "Logarithm Law",
            type: "formulas",
            description: "log₂(x) + log₂(x-2) = log₂(x(x-2))",
            value: "log₂(x(x-2)) = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x(x-2) = 2^3",
            value: "x(x-2) = 8",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "quadratic_equation",
            name: "Quadratic Equation",
            type: "values",
            description: "Expand and rearrange",
            value: "x² - 2x = 8, x² - 2x - 8 = 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "factoring",
            name: "Factoring",
            type: "values",
            description: "Factor the quadratic",
            value: "(x-4)(x+2) = 0",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve and check domain",
            value: "x = 4 (x = -2 is invalid)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_logarithms",
            name: "Identify Logarithms",
            type: "steps",
            description: "Recognize the two logarithmic terms",
            value: "Step 1: log₂(x) and log₂(x-2)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_logarithm_law",
            name: "Apply Logarithm Law",
            type: "steps",
            description: "Use the logarithm addition law",
            value: "Step 2: log₂(x(x-2)) = 3",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x(x-2) = 2^3 = 8",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_quadratic",
            name: "Solve Quadratic",
            type: "steps",
            description: "Solve the quadratic equation",
            value: "Step 4: x = 4 (check domain)",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "logarithm_law", type: "simplified using", strength: 9 },
          { from: "logarithm_law", to: "exponential_form", type: "converted to", strength: 10 },
          { from: "logarithm_to_exponential", to: "exponential_form", type: "gives", strength: 10 },
          { from: "exponential_form", to: "quadratic_equation", type: "gives", strength: 10 },
          { from: "quadratic_equation", to: "factoring", type: "solved by", strength: 10 },
          { from: "factoring", to: "solution", type: "gives", strength: 10 },
          { from: "identify_logarithms", to: "apply_logarithm_law", type: "enables", strength: 8 },
          { from: "apply_logarithm_law", to: "convert_to_exponential", type: "enables", strength: 9 },
          { from: "convert_to_exponential", to: "solve_quadratic", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_hard_03',
      problem: "Solve 3^(2x+1) = 9^(x-1)",
      problemType: "exponential_logarithm",
      difficulty: "harder",
      description: "Exponential equation with different bases",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with different bases",
            value: "3^(2x+1) = 9^(x-1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base_conversion",
            name: "Base Conversion",
            type: "formulas",
            description: "Convert 9 to base 3: 9 = 3^2",
            value: "9 = 3^2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "substitution",
            name: "Substitution",
            type: "values",
            description: "Substitute back into equation",
            value: "3^(2x+1) = (3^2)^(x-1)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponent_simplification",
            name: "Exponent Simplification",
            type: "values",
            description: "Simplify right side",
            value: "3^(2x+1) = 3^(2(x-1)) = 3^(2x-2)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "exponent_equation",
            name: "Exponent Equation",
            type: "values",
            description: "Set exponents equal",
            value: "2x + 1 = 2x - 2",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve the equation",
            value: "1 = -2 (no solution)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_bases",
            name: "Identify Bases",
            type: "steps",
            description: "Recognize the different bases",
            value: "Step 1: Base 3 and base 9",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "convert_base",
            name: "Convert Base",
            type: "steps",
            description: "Convert 9 to base 3",
            value: "Step 2: 9 = 3^2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 2x + 1 = 2x - 2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "check_solution",
            name: "Check Solution",
            type: "steps",
            description: "Check for contradiction",
            value: "Step 4: 1 = -2 (no solution)",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "base_conversion", type: "simplified using", strength: 9 },
          { from: "base_conversion", to: "substitution", type: "gives", strength: 10 },
          { from: "substitution", to: "exponent_simplification", type: "simplified to", strength: 10 },
          { from: "exponent_simplification", to: "exponent_equation", type: "gives", strength: 10 },
          { from: "exponent_equation", to: "solution", type: "solves", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_bases", to: "convert_base", type: "enables", strength: 8 },
          { from: "convert_base", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "check_solution", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_hard_04',
      problem: "Solve log₃(x² - 4) = 2",
      problemType: "exponential_logarithm",
      difficulty: "harder",
      description: "Logarithmic equation with quadratic argument",
      graphData: {
        nodes: [
          {
            id: "logarithmic_equation",
            name: "Logarithmic Equation",
            type: "concepts",
            description: "An equation with quadratic argument in logarithm",
            value: "log₃(x² - 4) = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the logarithm",
            value: "3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "The value of the logarithm",
            value: "2",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "logarithm_to_exponential",
            name: "Logarithm to Exponential Conversion",
            type: "formulas",
            description: "log_b(x) = y is equivalent to x = b^y",
            value: "log_b(x) = y ↔ x = b^y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "exponential_form",
            name: "Exponential Form",
            type: "formulas",
            description: "Convert to exponential: x² - 4 = 3^2",
            value: "x² - 4 = 9",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "quadratic_equation",
            name: "Quadratic Equation",
            type: "values",
            description: "Rearrange to standard form",
            value: "x² - 4 = 9, x² = 13",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = ±√13",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "identify_base",
            name: "Identify Base",
            type: "steps",
            description: "Recognize the base of the logarithm",
            value: "Step 1: Base is 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_exponent",
            name: "Identify Exponent",
            type: "steps",
            description: "Recognize the value of the logarithm",
            value: "Step 2: Exponent is 2",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_exponential",
            name: "Convert to Exponential",
            type: "steps",
            description: "Use the conversion formula",
            value: "Step 3: x² - 4 = 3^2 = 9",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_quadratic",
            name: "Solve Quadratic",
            type: "steps",
            description: "Solve the quadratic equation",
            value: "Step 4: x = ±√13",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "logarithmic_equation", to: "base", type: "has", strength: 9 },
          { from: "logarithmic_equation", to: "exponent", type: "equals", strength: 9 },
          { from: "logarithm_to_exponential", to: "exponential_form", type: "gives", strength: 10 },
          { from: "base", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponent", to: "exponential_form", type: "used in", strength: 10 },
          { from: "exponential_form", to: "quadratic_equation", type: "gives", strength: 10 },
          { from: "quadratic_equation", to: "solution", type: "solves", strength: 10 },
          { from: "identify_base", to: "identify_exponent", type: "enables", strength: 8 },
          { from: "identify_exponent", to: "convert_to_exponential", type: "enables", strength: 9 },
          { from: "convert_to_exponential", to: "solve_quadratic", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'exp_log_hard_05',
      problem: "Solve 2^(x+1) × 2^(x-1) = 16",
      problemType: "exponential_logarithm",
      difficulty: "harder",
      description: "Exponential equation with product of exponentials",
      graphData: {
        nodes: [
          {
            id: "exponential_equation",
            name: "Exponential Equation",
            type: "concepts",
            description: "An equation with product of exponentials",
            value: "2^(x+1) × 2^(x-1) = 16",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "exponent_law",
            name: "Exponent Law",
            type: "formulas",
            description: "2^(x+1) × 2^(x-1) = 2^((x+1)+(x-1)) = 2^(2x)",
            value: "2^(2x) = 16",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "exponential_equality_rule",
            name: "Exponential Equality Rule",
            type: "formulas",
            description: "If a^x = a^y, then x = y",
            value: "If a^x = a^y, then x = y",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "power_identification",
            name: "Power Identification",
            type: "values",
            description: "Identify 16 as a power of 2",
            value: "16 = 2^4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "exponent_equation",
            name: "Exponent Equation",
            type: "values",
            description: "Set exponents equal",
            value: "2x = 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve for x",
            value: "x = 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "identify_terms",
            name: "Identify Terms",
            type: "steps",
            description: "Recognize the two exponential terms",
            value: "Step 1: 2^(x+1) and 2^(x-1)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_exponent_law",
            name: "Apply Exponent Law",
            type: "steps",
            description: "Use the exponent multiplication law",
            value: "Step 2: 2^(x+1) × 2^(x-1) = 2^(2x)",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_equality_rule",
            name: "Apply Equality Rule",
            type: "steps",
            description: "Use the exponential equality rule",
            value: "Step 3: 2^(2x) = 2^4, so 2x = 4",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Solve the linear equation",
            value: "Step 4: x = 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "exponential_equation", to: "exponent_law", type: "simplified using", strength: 9 },
          { from: "exponent_law", to: "power_identification", type: "compared with", strength: 10 },
          { from: "power_identification", to: "exponent_equation", type: "gives", strength: 10 },
          { from: "exponent_equation", to: "solution", type: "solves", strength: 10 },
          { from: "exponential_equality_rule", to: "solution", type: "gives", strength: 10 },
          { from: "identify_terms", to: "apply_exponent_law", type: "enables", strength: 8 },
          { from: "apply_exponent_law", to: "apply_equality_rule", type: "enables", strength: 9 },
          { from: "apply_equality_rule", to: "solve_equation", type: "leads to", strength: 10 }
        ]
      }
    }
  ]
}; 